import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Percent from "_shared/percentages/percent";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/_shared/masked-fields";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import { RedlineChange } from "work/proposals/redlining/changes/redline-change";
import { FeeScheduleRedlineJSON } from "work/proposals/redlining/fee-schedule-redline/_schema/fee-schedule-redline-schema";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import {
  FieldRedlineArrayJSON,
  FieldRedlineJSON,
} from "work/proposals/redlining/field-redlines/_schemas/field-redline-schema";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";

export type ProposalRedlineJSON = {
  name: FieldRedlineJSON;
  description: FieldRedlineJSON;
  responseDueBy: FieldRedlineJSON;
  startDate: FieldRedlineJSON;
  endDate: FieldRedlineJSON;
  clientPolicies: FieldRedlineArrayJSON | { isMasked: true };
  vendorPolicies: FieldRedlineArrayJSON | { isMasked: true };
  conflictsCheckWaived: FieldRedlineJSON | { isMasked: true };
  teamRestricted: FieldRedlineJSON | { isMasked: true };
  conflicts: FieldRedlineArrayJSON | { isMasked: true };
  discount: FieldRedlineJSON | { isMasked: true };
  vendorTeam: FieldRedlineArrayJSON | { isMasked: true };
  feeSchedule: FeeScheduleRedlineJSON | { isMasked: true };
  lastUpdated: string;
};

export default class ProposalRedline {
  public static fromJSON(json: unknown): ProposalRedline | null | undefined {
    if (isNullOrUndefined(json)) return json;

    let parsedJSON: ProposalRedlineJSON;
    if (typeof json === "string") parsedJSON = JSON.parse(json) as ProposalRedlineJSON;
    else parsedJSON = json as ProposalRedlineJSON;

    return new ProposalRedline({
      name: FieldRedline.fromJSON(parsedJSON.name, ProposalField.Name),
      description: FieldRedline.fromJSON(parsedJSON.description, ProposalField.Description),
      responseDueBy: FieldRedline.fromJSON(parsedJSON.responseDueBy, ProposalField.ResponseDueBy),
      startDate: FieldRedline.fromJSON(parsedJSON.startDate, ProposalField.StartDate),
      endDate: FieldRedline.fromJSON(parsedJSON.endDate, ProposalField.EndDate),
      clientPolicies: Masked.isNotMasked(parsedJSON.clientPolicies)
        ? FieldRedlineArray.fromJSON(
            parsedJSON.clientPolicies,
            ProposalField.ClientPolicies,
            ProposalField.ClientPolicy,
          )
        : new MaskedContracts(),
      vendorPolicies: Masked.isNotMasked(parsedJSON.vendorPolicies)
        ? FieldRedlineArray.fromJSON(
            parsedJSON.vendorPolicies,
            ProposalField.VendorPolicies,
            ProposalField.VendorPolicy,
          )
        : new MaskedContracts(),
      conflictsCheckWaived: Masked.isNotMasked(parsedJSON.conflictsCheckWaived)
        ? FieldRedline.fromJSON(parsedJSON.conflictsCheckWaived, ProposalField.WaiveConflictsCheck)
        : new MaskedContracts(),
      teamRestricted: Masked.isNotMasked(parsedJSON.teamRestricted)
        ? FieldRedline.fromJSON(parsedJSON.teamRestricted, ProposalField.TeamRestriction)
        : new MaskedTeam(),
      conflicts: Masked.isNotMasked(parsedJSON.conflicts)
        ? FieldRedlineArray.fromJSON(parsedJSON.conflicts, ProposalField.Conflicts, ProposalField.ConflictsDocument)
        : new MaskedContracts(),
      discount: Masked.isNotMasked(parsedJSON.discount)
        ? FieldRedline.fromJSON(parsedJSON.discount, ProposalField.Discount)
        : new MaskedDiscount(),
      vendorTeam: Masked.isNotMasked(parsedJSON.vendorTeam)
        ? FieldRedlineArray.fromJSON(parsedJSON.vendorTeam, ProposalField.Team, ProposalField.TeamMember)
        : new MaskedTeam(),
      feeSchedule: Masked.isNotMasked(parsedJSON.feeSchedule)
        ? FeeScheduleRedline.fromJSONArray(parsedJSON.feeSchedule)
        : new MaskedFeeSchedule(),
      lastUpdated: new AHDate(parsedJSON.lastUpdated),
    });
  }

  public readonly name: FieldRedline<ProjectName>;
  public readonly description: FieldRedline<ProjectDescription>;
  public readonly responseDueBy: FieldRedline<AHDate>;
  public readonly startDate: FieldRedline<AHDate>;
  public readonly endDate: FieldRedline<AHDate>;
  public readonly clientPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
  public readonly vendorPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
  public readonly conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts;
  public readonly teamRestricted: FieldRedline<AHBoolean> | MaskedTeam;
  public readonly conflicts: FieldRedlineArray<WorkDocument> | MaskedContracts;
  public readonly discount: FieldRedline<Percent> | MaskedDiscount;
  public readonly lastUpdated: AHDate;
  public readonly vendorTeam: FieldRedlineArray<EntityMember> | MaskedTeam;
  public readonly feeSchedule: FeeScheduleRedline | MaskedFeeSchedule;

  public get hasRejections(): boolean {
    return (
      this.name.isRejected ||
      this.description.isRejected ||
      this.responseDueBy.isRejected ||
      this.startDate.isRejected ||
      this.endDate.isRejected ||
      (Masked.isNotMasked(this.vendorTeam) && this.vendorTeam.hasRejections) ||
      (Masked.isNotMasked(this.feeSchedule) && this.feeSchedule.hasRejections) ||
      (Masked.isNotMasked(this.clientPolicies) && this.clientPolicies.hasRejections) ||
      (Masked.isNotMasked(this.vendorPolicies) && this.vendorPolicies.hasRejections) ||
      (Masked.isNotMasked(this.conflictsCheckWaived) && this.conflictsCheckWaived.isRejected) ||
      (Masked.isNotMasked(this.teamRestricted) && this.teamRestricted.isRejected) ||
      (Masked.isNotMasked(this.conflicts) && this.conflicts.hasRejections) ||
      (Masked.isNotMasked(this.discount) && this.discount.isRejected)
    );
  }
  public get hasRevisions(): boolean {
    return (
      this.name.isRevised ||
      this.description.isRevised ||
      this.responseDueBy.isRevised ||
      this.startDate.isRevised ||
      this.endDate.isRevised ||
      (Masked.isNotMasked(this.vendorTeam) && this.vendorTeam.isRevised) ||
      (Masked.isNotMasked(this.feeSchedule) && this.feeSchedule.isRevised) ||
      (Masked.isNotMasked(this.clientPolicies) && this.clientPolicies.isRevised) ||
      (Masked.isNotMasked(this.vendorPolicies) && this.vendorPolicies.isRevised) ||
      (Masked.isNotMasked(this.conflictsCheckWaived) && this.conflictsCheckWaived.isRevised) ||
      (Masked.isNotMasked(this.teamRestricted) && this.teamRestricted.isRevised) ||
      (Masked.isNotMasked(this.conflicts) && this.conflicts.isRevised) ||
      (Masked.isNotMasked(this.discount) && this.discount.isRevised)
    );
  }
  public get hasChanges(): boolean {
    return (
      this.name.hasChanges ||
      this.description.hasChanges ||
      this.responseDueBy.hasChanges ||
      this.startDate.hasChanges ||
      this.endDate.hasChanges ||
      (Masked.isNotMasked(this.vendorTeam) && this.vendorTeam.hasChanges) ||
      (Masked.isNotMasked(this.feeSchedule) && this.feeSchedule.hasChanges) ||
      (Masked.isNotMasked(this.clientPolicies) && this.clientPolicies.hasChanges) ||
      (Masked.isNotMasked(this.vendorPolicies) && this.vendorPolicies.hasChanges) ||
      (Masked.isNotMasked(this.conflictsCheckWaived) && this.conflictsCheckWaived.hasChanges) ||
      (Masked.isNotMasked(this.teamRestricted) && this.teamRestricted.hasChanges) ||
      (Masked.isNotMasked(this.conflicts) && this.conflicts.hasChanges) ||
      (Masked.isNotMasked(this.discount) && this.discount.hasChanges)
    );
  }
  public get sessionHistory(): RedlineChange[] {
    return [
      ...this.name.sessionHistory,
      ...this.description.sessionHistory,
      ...this.responseDueBy.sessionHistory,
      ...this.startDate.sessionHistory,
      ...this.endDate.sessionHistory,
      ...(Masked.isNotMasked(this.vendorTeam) ? this.vendorTeam.sessionHistory : []),
      ...(Masked.isNotMasked(this.feeSchedule) ? this.feeSchedule.sessionHistory : []),
      ...(Masked.isNotMasked(this.clientPolicies) ? this.clientPolicies.sessionHistory : []),
      ...(Masked.isNotMasked(this.vendorPolicies) ? this.vendorPolicies.sessionHistory : []),
      ...(Masked.isNotMasked(this.conflictsCheckWaived) ? this.conflictsCheckWaived.sessionHistory : []),
      ...(Masked.isNotMasked(this.teamRestricted) ? this.teamRestricted.sessionHistory : []),
      ...(Masked.isNotMasked(this.conflicts) ? this.conflicts.sessionHistory : []),
      ...(Masked.isNotMasked(this.discount) ? this.discount.sessionHistory : []),
    ];
  }
  public get unresolvedFields(): Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]> {
    const unresolvedFields = new Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>();
    if (!this.name.isResolved) unresolvedFields.add(ProposalFieldName.Name);
    if (!this.description.isResolved) unresolvedFields.add(ProposalFieldName.Description);
    if (!this.responseDueBy.isResolved) unresolvedFields.add(ProposalFieldName.ResponseDueBy);
    if (!this.startDate.isResolved) unresolvedFields.add(ProposalFieldName.StartDate);
    if (!this.endDate.isResolved) unresolvedFields.add(ProposalFieldName.EndDate);
    if (Masked.isMasked(this.vendorTeam) || !this.vendorTeam.isResolved) unresolvedFields.add(ProposalFieldName.Team);
    if (Masked.isMasked(this.feeSchedule) || !this.feeSchedule.isResolved)
      unresolvedFields.add(ProposalFieldName.FeeSchedule);
    if (Masked.isMasked(this.clientPolicies) || !this.clientPolicies.isResolved)
      unresolvedFields.add(ProposalFieldName.ClientPolicies);
    if (Masked.isMasked(this.vendorPolicies) || !this.vendorPolicies.isResolved)
      unresolvedFields.add(ProposalFieldName.VendorPolicies);
    if (Masked.isMasked(this.conflictsCheckWaived) || !this.conflictsCheckWaived.isResolved)
      unresolvedFields.add(ProposalFieldName.WaiveConflictsCheck);
    if (Masked.isMasked(this.teamRestricted) || !this.teamRestricted.isResolved)
      unresolvedFields.add(ProposalFieldName.TeamRestriction);
    if (Masked.isMasked(this.conflicts) || !this.conflicts.isResolved)
      unresolvedFields.add(ProposalFieldName.Conflicts);
    if (Masked.isMasked(this.discount) || !this.discount.isResolved) unresolvedFields.add(ProposalFieldName.Discount);
    return unresolvedFields;
  }
  public get isResolved(): boolean {
    return (
      this.name.isResolved &&
      this.description.isResolved &&
      this.responseDueBy.isResolved &&
      this.startDate.isResolved &&
      this.endDate.isResolved &&
      (Masked.isMasked(this.vendorTeam) || this.vendorTeam.isResolved) &&
      (Masked.isMasked(this.feeSchedule) || this.feeSchedule.isResolved) &&
      (Masked.isMasked(this.clientPolicies) || this.clientPolicies.isResolved) &&
      (Masked.isMasked(this.vendorPolicies) || this.vendorPolicies.isResolved) &&
      (Masked.isMasked(this.conflictsCheckWaived) || this.conflictsCheckWaived.isResolved) &&
      (Masked.isMasked(this.teamRestricted) || this.teamRestricted.isResolved) &&
      (Masked.isMasked(this.conflicts) || this.conflicts.isResolved) &&
      (Masked.isMasked(this.discount) || this.discount.isResolved)
    );
  }
  public get canBeUndone(): boolean {
    return (
      this.name.canBeUndone ||
      this.description.canBeUndone ||
      this.responseDueBy.canBeUndone ||
      this.startDate.canBeUndone ||
      this.endDate.canBeUndone ||
      (Masked.isNotMasked(this.vendorTeam) && this.vendorTeam.canBeUndone) ||
      (Masked.isNotMasked(this.feeSchedule) && this.feeSchedule.canBeUndone) ||
      (Masked.isNotMasked(this.clientPolicies) && this.clientPolicies.canBeUndone) ||
      (Masked.isNotMasked(this.vendorPolicies) && this.vendorPolicies.canBeUndone) ||
      (Masked.isNotMasked(this.conflictsCheckWaived) && this.conflictsCheckWaived.canBeUndone) ||
      (Masked.isNotMasked(this.teamRestricted) && this.teamRestricted.canBeUndone) ||
      (Masked.isNotMasked(this.conflicts) && this.conflicts.canBeUndone) ||
      (Masked.isNotMasked(this.discount) && this.discount.canBeUndone)
    );
  }

  public get currentProjectDates(): {
    startDate?: AHDate;
    endDate?: AHDate;
  } {
    return {
      startDate: this.startDate.currentEntry ?? undefined,
      endDate: this.endDate.currentEntry ?? undefined,
    };
  }

  public get currentDocuments(): {
    clientPolicies?: WorkDocument[] | MaskedContracts;
    vendorPolicies?: WorkDocument[] | MaskedContracts;
    conflicts?: WorkDocument[] | MaskedContracts;
    conflictsCheckWaived?: AHBoolean | MaskedContracts;
  } {
    return {
      clientPolicies: Masked.isMasked(this.clientPolicies) ? new MaskedContracts() : this.clientPolicies.currentEntries,
      vendorPolicies: Masked.isMasked(this.vendorPolicies) ? new MaskedContracts() : this.vendorPolicies.currentEntries,
      conflicts: Masked.isMasked(this.conflicts) ? new MaskedContracts() : this.conflicts.currentEntries,
      conflictsCheckWaived: Masked.isMasked(this.conflictsCheckWaived)
        ? new MaskedContracts()
        : (this.conflictsCheckWaived.currentEntry ?? new AHBoolean(false)),
    };
  }

  public get currentFeeSchedule(): MaskedFeeSchedule | FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] {
    return Masked.isMasked(this.feeSchedule) ? new MaskedFeeSchedule() : this.feeSchedule.currentEntries;
  }

  public get currentTeamMembers(): MaskedTeam | EntityMember[] {
    return Masked.isMasked(this.vendorTeam) ? new MaskedTeam() : this.vendorTeam.currentEntries;
  }

  public get currentDiscount(): MaskedDiscount | Percent {
    return Masked.isMasked(this.discount) ? new MaskedDiscount() : this.discount.currentEntry!;
  }

  constructor(params: {
    name: FieldRedline<ProjectName>;
    description: FieldRedline<ProjectDescription>;
    responseDueBy: FieldRedline<AHDate>;
    startDate: FieldRedline<AHDate>;
    endDate: FieldRedline<AHDate>;
    vendorTeam: FieldRedlineArray<EntityMember> | MaskedTeam;
    feeSchedule: FeeScheduleRedline | MaskedFeeSchedule;
    clientPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
    vendorPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
    conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts;
    teamRestricted: FieldRedline<AHBoolean> | MaskedTeam;
    conflicts: FieldRedlineArray<WorkDocument> | MaskedContracts;
    discount: FieldRedline<Percent> | MaskedDiscount;
    lastUpdated?: AHDate;
  }) {
    this.name = params.name;
    this.description = params.description;
    this.responseDueBy = params.responseDueBy;
    this.startDate = params.startDate;
    this.endDate = params.endDate;
    this.vendorTeam = params.vendorTeam;
    this.feeSchedule = params.feeSchedule;
    this.clientPolicies = params.clientPolicies;
    this.vendorPolicies = params.vendorPolicies;
    this.conflictsCheckWaived = params.conflictsCheckWaived;
    this.teamRestricted = params.teamRestricted;
    this.conflicts = params.conflicts;
    this.discount = params.discount;
    this.lastUpdated = params.lastUpdated ?? new AHDate();
  }

  public accept(): ProposalRedline {
    return new ProposalRedline({
      name: this.name.accept(),
      description: this.description.accept(),
      vendorTeam: Masked.isNotMasked(this.vendorTeam) ? this.vendorTeam.acceptAll() : this.vendorTeam,
      feeSchedule: Masked.isNotMasked(this.feeSchedule) ? this.feeSchedule.acceptAll() : this.feeSchedule,
      conflictsCheckWaived: Masked.isNotMasked(this.conflictsCheckWaived)
        ? this.conflictsCheckWaived.accept()
        : this.conflictsCheckWaived,
      teamRestricted: Masked.isNotMasked(this.teamRestricted) ? this.teamRestricted.accept() : this.teamRestricted,
      discount: Masked.isNotMasked(this.discount) ? this.discount.accept() : this.discount,
      responseDueBy: this.responseDueBy.accept(),
      startDate: this.startDate.accept(),
      endDate: this.endDate.accept(),
      clientPolicies: Masked.isNotMasked(this.clientPolicies) ? this.clientPolicies.acceptAll() : this.clientPolicies,
      vendorPolicies: Masked.isNotMasked(this.vendorPolicies) ? this.vendorPolicies.acceptAll() : this.vendorPolicies,
      conflicts: Masked.isNotMasked(this.conflicts) ? this.conflicts.acceptAll() : this.conflicts,
      lastUpdated: new AHDate(),
    });
  }

  public reject(): ProposalRedline {
    return new ProposalRedline({
      name: this.name.reject(),
      description: this.description.reject(),
      vendorTeam: Masked.isNotMasked(this.vendorTeam) ? this.vendorTeam.rejectAll() : this.vendorTeam,
      feeSchedule: Masked.isNotMasked(this.feeSchedule) ? this.feeSchedule.rejectAll() : this.feeSchedule,
      conflictsCheckWaived: Masked.isNotMasked(this.conflictsCheckWaived)
        ? this.conflictsCheckWaived.reject()
        : this.conflictsCheckWaived,
      teamRestricted: Masked.isNotMasked(this.teamRestricted) ? this.teamRestricted.reject() : this.teamRestricted,
      discount: Masked.isNotMasked(this.discount) ? this.discount.reject() : this.discount,
      responseDueBy: this.responseDueBy.reject(),
      startDate: this.startDate.reject(),
      endDate: this.endDate.reject(),
      clientPolicies: Masked.isNotMasked(this.clientPolicies) ? this.clientPolicies.rejectAll() : this.clientPolicies,
      vendorPolicies: Masked.isNotMasked(this.vendorPolicies) ? this.vendorPolicies.rejectAll() : this.vendorPolicies,
      conflicts: Masked.isNotMasked(this.conflicts) ? this.conflicts.rejectAll() : this.conflicts,
      lastUpdated: new AHDate(),
    });
  }

  public undo(): ProposalRedline {
    return new ProposalRedline({
      name: this.name.undo(),
      description: this.description.undo(),
      vendorTeam: Masked.isNotMasked(this.vendorTeam) ? this.vendorTeam.undoAll() : this.vendorTeam,
      feeSchedule: Masked.isNotMasked(this.feeSchedule) ? this.feeSchedule.undoAll() : this.feeSchedule,
      conflictsCheckWaived: Masked.isNotMasked(this.conflictsCheckWaived)
        ? this.conflictsCheckWaived.undo()
        : this.conflictsCheckWaived,
      teamRestricted: Masked.isNotMasked(this.teamRestricted) ? this.teamRestricted.undo() : this.teamRestricted,
      discount: Masked.isNotMasked(this.discount) ? this.discount.undo() : this.discount,
      responseDueBy: this.responseDueBy.undo(),
      startDate: this.startDate.undo(),
      endDate: this.endDate.undo(),
      clientPolicies: Masked.isNotMasked(this.clientPolicies) ? this.clientPolicies.undoAll() : this.clientPolicies,
      vendorPolicies: Masked.isNotMasked(this.vendorPolicies) ? this.vendorPolicies.undoAll() : this.vendorPolicies,
      conflicts: Masked.isNotMasked(this.conflicts) ? this.conflicts.undoAll() : this.conflicts,
      lastUpdated: new AHDate(),
    });
  }

  public updateNameRedline = (newNameRedline: FieldRedline<ProjectName>): ProposalRedline =>
    new ProposalRedline({ ...this, name: newNameRedline });

  public updateDescriptionRedline = (newDescriptionRedline: FieldRedline<ProjectDescription>): ProposalRedline =>
    new ProposalRedline({ ...this, description: newDescriptionRedline });

  public updateResponseDueByRedline = (newResponseDueByRedline: FieldRedline<AHDate>): ProposalRedline =>
    new ProposalRedline({
      ...this,
      responseDueBy: newResponseDueByRedline,
    });

  public updateStartDateRedline = (newStartDateRedline: FieldRedline<AHDate>): ProposalRedline =>
    new ProposalRedline({
      ...this,
      startDate: newStartDateRedline,
    });

  public updateEndDateRedline = (newEndDateRedline: FieldRedline<AHDate>): ProposalRedline =>
    new ProposalRedline({
      ...this,
      endDate: newEndDateRedline,
    });

  public updateTeamRestrictedRedline = (newTeamRestrictedRedline: FieldRedline<AHBoolean>): ProposalRedline =>
    new ProposalRedline({ ...this, teamRestricted: newTeamRestrictedRedline });

  public updateTeamMembersRedline(newTeamRedline: FieldRedlineArray<EntityMember>): ProposalRedline {
    return new ProposalRedline({
      ...this,
      vendorTeam: newTeamRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateFeeScheduleRedline(newFeeScheduleRedline: FeeScheduleRedline): ProposalRedline {
    return new ProposalRedline({
      ...this,
      feeSchedule: newFeeScheduleRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateConflictsCheckWaivedRedline(newConflictsCheckWaivedRedline: FieldRedline<AHBoolean>): ProposalRedline {
    if (Masked.isMasked(this.conflictsCheckWaived) || Masked.isMasked(this.conflicts)) {
      throw new Error("Cannot update conflicts check waived redline when it is masked.");
    }
    const acceptedWaiving =
      this.conflictsCheckWaived.originalEntry?.value === false &&
      newConflictsCheckWaivedRedline.revisedEntry?.value === true &&
      newConflictsCheckWaivedRedline.isAccepted;
    const rejectedUnwaiving =
      this.conflictsCheckWaived.originalEntry?.value === true &&
      newConflictsCheckWaivedRedline.revisedEntry?.value === false &&
      newConflictsCheckWaivedRedline.isRejected;

    let conflicts = this.conflicts;
    if (acceptedWaiving || rejectedUnwaiving) {
      conflicts = this.conflicts.removeAll();
    }
    return new ProposalRedline({
      ...this,
      conflictsCheckWaived: newConflictsCheckWaivedRedline,
      conflicts,
      lastUpdated: new AHDate(),
    });
  }

  public updateConflictsRedline(newConflictsRedline: FieldRedlineArray<WorkDocument>): ProposalRedline {
    return new ProposalRedline({
      ...this,
      conflicts: newConflictsRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateClientPoliciesRedline(newClientPoliciesRedline: FieldRedlineArray<WorkDocument>): ProposalRedline {
    return new ProposalRedline({
      ...this,
      clientPolicies: newClientPoliciesRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateVendorPoliciesRedline(newVendorPoliciesRedline: FieldRedlineArray<WorkDocument>): ProposalRedline {
    return new ProposalRedline({
      ...this,
      vendorPolicies: newVendorPoliciesRedline,
      lastUpdated: new AHDate(),
    });
  }

  public updateDiscountRedline(newDiscountRedline: FieldRedline<Percent>): ProposalRedline {
    return new ProposalRedline({
      ...this,
      discount: newDiscountRedline,
      lastUpdated: new AHDate(),
    });
  }

  public isEqualTo(other?: ProposalRedline): boolean {
    return (
      this.name.isEqualTo(other?.name) &&
      this.description.isEqualTo(other?.description) &&
      this.responseDueBy.isEqualTo(other?.responseDueBy) &&
      this.startDate.isEqualTo(other?.startDate) &&
      this.endDate.isEqualTo(other?.endDate) &&
      Masked.isEqual(this.vendorTeam, other?.vendorTeam) &&
      Masked.isEqual(this.feeSchedule, other?.feeSchedule) &&
      Masked.isEqual(this.clientPolicies, other?.clientPolicies) &&
      Masked.isEqual(this.vendorPolicies, other?.vendorPolicies) &&
      Masked.isEqual(this.conflictsCheckWaived, other?.conflictsCheckWaived) &&
      Masked.isEqual(this.teamRestricted, other?.teamRestricted) &&
      Masked.isEqual(this.conflicts, other?.conflicts) &&
      Masked.isEqual(this.discount, other?.discount)
    );
  }

  public toJSON(): ProposalRedlineJSON {
    return {
      name: this.name.toJSON(),
      description: this.description.toJSON(),
      responseDueBy: this.responseDueBy.toJSON(),
      startDate: this.startDate.toJSON(),
      endDate: this.endDate.toJSON(),
      vendorTeam: Masked.isNotMasked(this.vendorTeam) ? this.vendorTeam.toJSON() : { isMasked: true },
      teamRestricted: Masked.isNotMasked(this.teamRestricted) ? this.teamRestricted.toJSON() : { isMasked: true },
      feeSchedule: Masked.isNotMasked(this.feeSchedule) ? this.feeSchedule.toJSON() : { isMasked: true },
      clientPolicies: Masked.isNotMasked(this.clientPolicies) ? this.clientPolicies.toJSON() : { isMasked: true },
      vendorPolicies: Masked.isNotMasked(this.vendorPolicies) ? this.vendorPolicies.toJSON() : { isMasked: true },
      conflictsCheckWaived: Masked.isNotMasked(this.conflictsCheckWaived)
        ? this.conflictsCheckWaived.toJSON()
        : { isMasked: true },
      conflicts: Masked.isNotMasked(this.conflicts) ? this.conflicts.toJSON() : { isMasked: true },
      discount: Masked.isNotMasked(this.discount) ? this.discount.toJSON() : { isMasked: true },
      lastUpdated: this.lastUpdated.toISOString(),
    };
  }
}

export type RedlinedDocuments = {
  clientPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
  vendorPolicies: FieldRedlineArray<WorkDocument> | MaskedContracts;
  conflicts: FieldRedlineArray<WorkDocument> | MaskedContracts;
};

export type RedlinedDates = {
  startDate: FieldRedline<AHDate>;
  endDate: FieldRedline<AHDate>;
  responseDueBy: FieldRedline<AHDate>;
};
